import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  SparklesIcon,
  ChartBarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import { learningAPI } from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';

export default function AIInsights() {
  const [selectedMember, setSelectedMember] = useState(null);
  const [riskParams, setRiskParams] = useState({
    project_type: 'web_app',
    team_size: 5,
    budget: 100000,
    timeline: 90,
  });

  // Fetch AI learning insights
  const { data: insights, isLoading: insightsLoading, error: insightsError } = useQuery({
    queryKey: ['learning-insights'],
    queryFn: learningAPI.getInsights,
  });

  // Fetch team member performance if selected
  useQuery({
    queryKey: ['team-performance', selectedMember],
    queryFn: () => learningAPI.getTeamMemberPerformance(selectedMember),
    enabled: !!selectedMember,
  });

  if (insightsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (insightsError) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading AI insights: {insightsError.message}</p>
        </div>
      </div>
    );
  }

  const stats = [
    {
      name: 'Total Projects Analyzed',
      value: insights?.total_projects || 0,
      icon: ChartBarIcon,
      color: 'blue',
      description: 'Projects used for AI learning',
    },
    {
      name: 'Prediction Accuracy',
      value: insights?.accuracy ? `${(insights.accuracy * 100).toFixed(1)}%` : '0%',
      icon: SparklesIcon,
      color: 'purple',
      description: 'AI estimation accuracy',
    },
    {
      name: 'Average Improvement',
      value: insights?.avg_improvement ? `${(insights.avg_improvement * 100).toFixed(1)}%` : '0%',
      icon: ArrowTrendingUpIcon,
      color: 'green',
      description: 'Time saved vs. original estimates',
    },
    {
      name: 'Learning Model Version',
      value: insights?.model_version || 'v1.0',
      icon: LightBulbIcon,
      color: 'yellow',
      description: 'Current AI model version',
    },
  ];

  const handleRiskPrediction = async () => {
    // This would trigger risk prediction with current params
    console.log('Risk prediction params:', riskParams);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <SparklesIcon className="h-8 w-8 text-purple-600 mr-3" />
            AI Insights Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            Real-time AI learning metrics and predictions
          </p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-2">
          <div className="flex items-center">
            <div className="h-3 w-3 bg-purple-500 rounded-full animate-pulse mr-2"></div>
            <span className="text-sm font-medium text-purple-700">AI Active</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.name}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 bg-${stat.color}-100 rounded-lg`}>
                  <Icon className={`h-6 w-6 text-${stat.color}-600`} />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600">{stat.name}</h3>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
              <p className="text-sm text-gray-500 mt-1">{stat.description}</p>
            </div>
          );
        })}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Learning Performance Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <ChartBarIcon className="h-5 w-5 text-blue-600 mr-2" />
              Learning Performance
            </h2>
          </div>

          {insights?.recent_predictions && insights.recent_predictions.length > 0 ? (
            <div className="space-y-3">
              {insights.recent_predictions.slice(0, 5).map((pred, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{pred.project_type}</p>
                    <p className="text-sm text-gray-600">
                      Estimated: {pred.estimated_days} days
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${pred.accuracy > 0.8 ? 'text-green-600' : 'text-yellow-600'}`}>
                      {(pred.accuracy * 100).toFixed(0)}% accurate
                    </p>
                    {pred.actual_days && (
                      <p className="text-sm text-gray-600">
                        Actual: {pred.actual_days} days
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No prediction data available yet</p>
              <p className="text-sm text-gray-500 mt-1">
                Complete projects to train the AI model
              </p>
            </div>
          )}
        </div>

        {/* Risk Prediction Tool */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
              Risk Prediction Tool
            </h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Type
              </label>
              <select
                value={riskParams.project_type}
                onChange={(e) => setRiskParams({ ...riskParams, project_type: e.target.value })}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="web_app">Web Application</option>
                <option value="mobile_app">Mobile App</option>
                <option value="api_service">API Service</option>
                <option value="data_pipeline">Data Pipeline</option>
                <option value="microservices">Microservices</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Size
                </label>
                <input
                  type="number"
                  value={riskParams.team_size}
                  onChange={(e) => setRiskParams({ ...riskParams, team_size: parseInt(e.target.value) })}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  min="1"
                  max="50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Budget ($)
                </label>
                <input
                  type="number"
                  value={riskParams.budget}
                  onChange={(e) => setRiskParams({ ...riskParams, budget: parseFloat(e.target.value) })}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  min="1000"
                  step="1000"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timeline (days)
              </label>
              <input
                type="number"
                value={riskParams.timeline}
                onChange={(e) => setRiskParams({ ...riskParams, timeline: parseInt(e.target.value) })}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                min="7"
                max="365"
              />
            </div>

            <button
              onClick={handleRiskPrediction}
              className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center"
            >
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
              Analyze Risks
            </button>

            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Risk prediction uses historical data from {insights?.total_projects || 0} completed projects to identify potential issues.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Team Performance Leaderboard */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <UserGroupIcon className="h-5 w-5 text-green-600 mr-2" />
            Team Performance Leaderboard
          </h2>
          <span className="text-sm text-gray-600">Last 30 days</span>
        </div>

        {insights?.top_performers && insights.top_performers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tasks Completed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Accuracy
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance Score
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {insights.top_performers.map((performer, idx) => (
                  <tr
                    key={performer.member_id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedMember(performer.member_id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {idx === 0 && <span className="text-2xl mr-2">🥇</span>}
                        {idx === 1 && <span className="text-2xl mr-2">🥈</span>}
                        {idx === 2 && <span className="text-2xl mr-2">🥉</span>}
                        {idx > 2 && <span className="text-gray-600 font-medium mr-2">#{idx + 1}</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{performer.name}</div>
                      <div className="text-sm text-gray-500">{performer.role}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                        <span className="text-sm font-medium text-gray-900">{performer.tasks_completed}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        performer.accuracy > 0.9 ? 'bg-green-100 text-green-800' :
                        performer.accuracy > 0.7 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {(performer.accuracy * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-32 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${performer.score * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {(performer.score * 100).toFixed(0)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No team performance data available yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Team members will appear here as they complete tasks
            </p>
          </div>
        )}
      </div>

      {/* AI Recommendations */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6">
        <div className="flex items-start">
          <LightBulbIcon className="h-6 w-6 text-purple-600 mr-3 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              AI Recommendations
            </h3>
            <div className="space-y-2">
              {insights?.recommendations && insights.recommendations.length > 0 ? (
                insights.recommendations.map((rec, idx) => (
                  <div key={idx} className="flex items-start">
                    <span className="text-purple-600 mr-2">•</span>
                    <p className="text-gray-700">{rec}</p>
                  </div>
                ))
              ) : (
                <>
                  <div className="flex items-start">
                    <span className="text-purple-600 mr-2">•</span>
                    <p className="text-gray-700">
                      Complete more projects to receive personalized AI recommendations
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-600 mr-2">•</span>
                    <p className="text-gray-700">
                      The AI model learns from your team's performance patterns
                    </p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-600 mr-2">•</span>
                    <p className="text-gray-700">
                      Insights improve with each completed project milestone
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
